// Merchant name normalization.
//
// Bank descriptors are a mess: processor prefixes ("SQ *"), store and
// reference numbers, city/state tails, truncated words, and ALL CAPS. This
// module turns one into a name a person would recognize, and derives the
// canonical grouping key from that same name so display and matching can
// never disagree.
//
// Pipeline (each stage is independently testable):
//   1. unwrapProcessor  — "SQ *BLUE BOTTLE" → "BLUE BOTTLE"
//   2. matchAlias       — word-bounded brand table, most specific first
//   3. trimSegments     — spaced dashes, "*" refs, "/" paths, domains
//   4. tokenize         — drop noise / reference / corporate / location tokens
//   5. smartTitleCase   — acronyms, Mc/O', hyphen parts, small words

import { normalizeDesc, isReferenceToken } from './forecasting/series-key.js';

// ── 1. Payment processors ───────────────────────────────────────────
// Facilitators that prefix the *real* merchant, so the name lives after the
// marker. Brands that merely bill through their own gateway (Amazon, Google,
// Apple) are deliberately absent: for "GOOGLE *YOUTUBEPREMIUM" the brand is
// the recognizable part, and the suffix is an unsplittable slug.
const PROCESSOR_CODES = new Set([
  'sq', 'sqc', 'squ', 'tst', 'toast', 'py', 'pypl', 'paypal', 'pp', 'in', 'sp',
  'wpy', 'clv', 'clover', 'ec', 'dri', 'wl', 'sumup', 'izettle', 'stripe',
  'str', 'shopify', 'shp', 'chownow', 'slice', 'spothopper', 'gsuite',
]);

// "SQ *NAME", "TST* NAME", "PAYPAL *NAME" — the star is the reliable marker.
const STARRED_PREFIX = /^([a-z]{2,8})[\s.]*\*+\s*(?=\S)/i;
// Shopify writes "SP MERCHANT" with no star. Restricted to `sp` on purpose:
// allowing other two-letter codes here would eat real first words.
const SPACED_PREFIX = /^sp\s+(?=\S)/i;

function unwrapProcessor(s) {
  let out = s;
  // Chains happen: "PY *SQ *MERCHANT". Bounded to avoid pathological input.
  for (let i = 0; i < 3; i += 1) {
    const starred = out.match(STARRED_PREFIX);
    if (starred && PROCESSOR_CODES.has(starred[1].toLowerCase())) {
      out = out.slice(starred[0].length);
      continue;
    }
    if (SPACED_PREFIX.test(out)) {
      out = out.replace(SPACED_PREFIX, '');
      continue;
    }
    break;
  }
  return out.trim() || s;
}

// ── 2. Brand aliases ────────────────────────────────────────────────
// Ordered: the first match wins, so specific entries precede general ones
// ("uber eats" before "uber"). Every pattern is word-bounded — an unbounded
// `includes` turns "CHICKEN PALACE" into Chick-fil-A and "SHELLYS DINER"
// into Shell.
const DISPLAY_ALIASES = [
  // Groceries
  [/\btrader\s?joe'?s?\b|\btraderjoe/, "Trader Joe's"],
  [/\bwhole\s?foods?\b|\bwholefds?\b|\bwfm\b/, 'Whole Foods'],
  [/\bsam'?s\s?club\b/, "Sam's Club"],
  [/\bh[\s-]?e[\s-]?b\b/, 'H-E-B'],
  [/\bharris\s?teeter\b/, 'Harris Teeter'],
  [/\bfood\s?lion\b/, 'Food Lion'],
  [/\bwalmart\b|\bwal[\s-]?mart\b|\bwm\s?supercenter\b/, 'Walmart'],
  [/\bcostco\b/, 'Costco'],
  [/\bkroger\b/, 'Kroger'],
  [/\bsafeway\b/, 'Safeway'],
  [/\balbertsons?\b/, 'Albertsons'],
  [/\baldi\b/, 'Aldi'],
  [/\bpublix\b/, 'Publix'],
  [/\bwegmans\b/, 'Wegmans'],
  [/\bsprouts\b/, 'Sprouts'],
  [/\bmeijer\b/, 'Meijer'],
  [/\bwinco\b/, 'WinCo'],
  [/\btrader\s?jo\b/, "Trader Joe's"],

  // Eating out
  [/\bchick[\s-]?fil[\s-]?a\b|\bchickfila\b/, 'Chick-fil-A'],
  [/\bmc\s?donald'?s?\b|\bmcdonalds?\b/, "McDonald's"],
  [/\bstarbucks\b|\bsbux\b/, 'Starbucks'],
  [/\bdutch\s?bros\b/, 'Dutch Bros'],
  [/\bdunkin'?\b/, 'Dunkin'],
  [/\bchipotle\b/, 'Chipotle'],
  [/\btaco\s?bell\b/, 'Taco Bell'],
  [/\bburger\s?king\b/, 'Burger King'],
  [/\bin[\s-]?n[\s-]?out\b/, 'In-N-Out Burger'],
  [/\bwhataburger\b/, 'Whataburger'],
  [/\bfive\s?guys\b/, 'Five Guys'],
  [/\braising\s?cane'?s?\b/, "Raising Cane's"],
  [/\bpopeyes?\b/, 'Popeyes'],
  [/\bculver'?s\b/, "Culver's"],
  [/\bwendy'?s\b/, "Wendy's"],
  [/\bdomino'?s\b/, "Domino's"],
  [/\bpapa\s?john'?s?\b/, "Papa John's"],
  [/\bpizza\s?hut\b/, 'Pizza Hut'],
  [/\bjimmy\s?john'?s?\b/, "Jimmy John's"],
  [/\bjersey\s?mike'?s?\b/, "Jersey Mike's"],
  [/\bpanera\b/, 'Panera'],
  [/\bsubway\b/, 'Subway'],
  [/\bcrumbl\b/, 'Crumbl Cookies'],
  [/\bsonic\s?drive\b/, 'Sonic'],
  [/\barby'?s\b/, "Arby's"],
  [/\bcava\b/, 'Cava'],
  [/\bshake\s?shack\b/, 'Shake Shack'],

  // Delivery
  [/\bdoordash\b|\bdd\s?doordash\b/, 'DoorDash'],
  [/\bubereats\b|\buber\s?eats\b/, 'Uber Eats'],
  [/\bgrubhub\b/, 'Grubhub'],
  [/\binstacart\b/, 'Instacart'],
  [/\bpostmates\b/, 'Postmates'],
  [/\bseamless\b/, 'Seamless'],
  [/\bshipt\b/, 'Shipt'],

  // Transport & fuel
  [/\buber\s?trip\b|\buber\b/, 'Uber'],
  [/\blyft\b/, 'Lyft'],
  [/\b7[\s-]?eleven\b|\bseven\s?eleven\b/, '7-Eleven'],
  [/\bcircle\s?k\b/, 'Circle K'],
  [/\bquiktrip\b|\bqt\s?\d/, 'QuikTrip'],
  [/\bchevron\b/, 'Chevron'],
  [/\bexxon(mobil)?\b/, 'Exxon'],
  [/\bshell\s?oil\b|\bshell\b/, 'Shell'],
  [/\bwawa\b/, 'Wawa'],
  [/\bsheetz\b/, 'Sheetz'],
  [/\bspeedway\b/, 'Speedway'],
  [/\bracetrac\b/, 'RaceTrac'],
  [/\bbuc[\s-]?ee'?s\b/, "Buc-ee's"],
  [/\bsunoco\b/, 'Sunoco'],
  [/\bu[\s-]?haul\b/, 'U-Haul'],

  // Subscriptions & digital
  [/\bamazon\s?prime\b|\bprime\s?video\b/, 'Amazon Prime'],
  [/\bamazon\b|\bamzn\b/, 'Amazon'],
  [/\bnetflix\b/, 'Netflix'],
  [/\bspotify\b/, 'Spotify'],
  [/\bhulu\b/, 'Hulu'],
  [/\bdisney\s?(\+|plus)\b/, 'Disney+'],
  [/\byoutube\s?(premium|tv)\b|\byoutubepremium\b/, 'YouTube Premium'],
  [/\bapple\.?com\b|\bapple\s?(bill|services)\b|\bitunes\b/, 'Apple'],
  [/\bicloud\b/, 'iCloud'],
  [/\bmicrosoft\b|\bmsft\b/, 'Microsoft'],
  [/\badobe\b/, 'Adobe'],
  [/\bdropbox\b/, 'Dropbox'],
  [/\bopenai\b|\bchatgpt\b/, 'OpenAI'],
  [/\baudible\b/, 'Audible'],
  [/\bpatreon\b/, 'Patreon'],
  [/\bsteam\s?games\b|\bsteamgames\b|\bsteampowered\b/, 'Steam'],
  [/\bplaystation\b|\bpsn\b/, 'PlayStation'],
  [/\bnintendo\b/, 'Nintendo'],
  [/\bgoogle\b/, 'Google'],

  // Retail
  [/\btarget\b/, 'Target'],
  [/\bbest\s?buy\b/, 'Best Buy'],
  [/\bhome\s?depot\b|\bthe\s?home\s?depot\b/, 'The Home Depot'],
  [/\blowe'?s\b/, "Lowe's"],
  [/\back?e\s?hardware\b/, 'Ace Hardware'],
  [/\bikea\b/, 'IKEA'],
  [/\bwayfair\b/, 'Wayfair'],
  [/\betsy\b/, 'Etsy'],
  [/\bebay\b/, 'eBay'],
  [/\bnewegg\b/, 'Newegg'],
  [/\bt\.?j\.?\s?maxx\b|\btjmaxx\b/, 'TJ Maxx'],
  [/\bmarshalls\b/, 'Marshalls'],
  [/\bnordstrom\b/, 'Nordstrom'],
  [/\bmacy'?s\b/, "Macy's"],
  [/\bkohl'?s\b/, "Kohl's"],
  [/\bold\s?navy\b/, 'Old Navy'],
  [/\blululemon\b/, 'Lululemon'],
  [/\bsephora\b/, 'Sephora'],
  [/\bulta\b/, 'Ulta'],
  [/\bpetco\b/, 'Petco'],
  [/\bpetsmart\b/, 'PetSmart'],
  [/\bchewy\b/, 'Chewy'],
  [/\bmichaels\b/, 'Michaels'],
  [/\bhobby\s?lobby\b/, 'Hobby Lobby'],
  [/\bbarnes\s?(&|and)?\s?noble\b/, 'Barnes & Noble'],
  [/\bgamestop\b/, 'GameStop'],
  [/\bdick'?s\s?sporting\b/, "Dick's Sporting Goods"],
  [/\bcvs\b/, 'CVS'],
  [/\bwalgreens\b/, 'Walgreens'],
  [/\brite\s?aid\b/, 'Rite Aid'],

  // Bills, telco, money movement
  [/\bat\s?&\s?t\b|\batt\s?(bill|payment|mobility)\b|\bat&t\b/, 'AT&T'],
  [/\bt[\s-]?mobile\b|\btmobile\b/, 'T-Mobile'],
  [/\bverizon\b|\bvzw\b/, 'Verizon'],
  [/\bxfinity\b|\bcomcast\b/, 'Xfinity'],
  [/\bspectrum\b/, 'Spectrum'],
  [/\bpg\s?&\s?e\b|\bpge\b/, 'PG&E'],
  [/\bduke\s?energy\b/, 'Duke Energy'],
  [/\bpaypal\b|\bpypl\b/, 'PayPal'],
  [/\bvenmo\b/, 'Venmo'],
  [/\bzelle\b/, 'Zelle'],
  [/\bcash\s?app\b|\bsquare\s?cash\b/, 'Cash App'],
  [/\bplanet\s?fitness\b/, 'Planet Fitness'],
  [/\bla\s?fitness\b/, 'LA Fitness'],
  [/\bpeloton\b/, 'Peloton'],
  [/\bairbnb\b/, 'Airbnb'],
  [/\bticketmaster\b/, 'Ticketmaster'],
  [/\busps\b|\bus\s?postal\b/, 'USPS'],
];

function matchAlias(s) {
  // "*" and "/" join words that belong to one brand ("UBER *EATS",
  // "CVS/PHARMACY"); treat them as spaces so bounded patterns still match.
  const hay = normalizeDesc(String(s || '').replace(/[*/]+/g, ' '));
  if (!hay) return null;
  const hit = DISPLAY_ALIASES.find(([re]) => re.test(hay));
  return hit ? hit[1] : null;
}

// ── 3. Segment trimming ─────────────────────────────────────────────
// A *spaced* dash separates fields ("MERCHANT - REF"); a tight hyphen is part
// of the name ("IN-N-OUT", "T-MOBILE"), so it is never a split point.
const SPACED_DASH = /\s+[-–—]\s+/;

function trimSegments(s) {
  let out = s.split(SPACED_DASH)[0];
  // Any remaining "*" is a reference marker on a non-processor descriptor
  // ("AMAZON.COM*2X9AB"), so the name is what precedes it.
  out = out.split('*')[0];
  // Column padding in exported statements.
  out = out.split(/\s{2,}/)[0];
  // "CVS/PHARMACY", "APPLE.COM/BILL" — the qualifier after the slash is noise.
  if (/\S\/\S/.test(out)) {
    const head = out.split('/')[0].trim();
    if (head.length >= 2) out = head;
  }
  // Domains: "WWW.NEWEGG.COM" → "NEWEGG".
  out = out.replace(/\bwww\.|\bhttps?:\/\//gi, '');
  out = out.replace(/\.(com|net|org|io|co|us|shop|store)\b/gi, '');
  // Gateway strings are dot-delimited ("US.STORE.BAMBULAB"). Once the domain
  // suffixes above are gone, a dot still sitting between two letters is a
  // separator rather than part of a domain, so each segment becomes its own
  // token. A leading country code is a routing artifact, not part of the
  // brand — dropped only in this dot-delimited form, so space-separated names
  // ("US BANK") keep theirs.
  if (/[a-z]\.[a-z]/i.test(out)) {
    const segments = out.split('.').map((p) => p.trim()).filter(Boolean);
    if (segments.length > 1 && /^usa?$/i.test(segments[0])) segments.shift();
    out = segments.join(' ');
  }
  return out.trim() || s;
}

// ── 4. Tokens ───────────────────────────────────────────────────────
// Phrases have to go before word-level filtering, since neither half is noise
// on its own.
const NOISE_PHRASES = [
  /\bdirect\s?dep(osit)?\b/gi,
  /\bpayroll\s?\d*\b/gi,
  /\bpre[\s-]?auth(orized)?\b/gi,
  /\bcheck\s?card\b/gi,
  /\brecurring\s?(payment|debit)?\b/gi,
  /\b(co|des|indn)\s?id:?\s*\w*/gi,
];

const NOISE_WORDS = new Set([
  'pos', 'debit', 'credit', 'purchase', 'payment', 'ach', 'checkcard', 'visa',
  'mastercard', 'amex', 'pending', 'auth', 'card', 'osv', 'ppd', 'web', 'epay',
  'autopay', 'billpay', 'eft', 'ccd', 'ctx', 'sec', 'chk', 'id', 'ref', 'seq',
  'trace', 'xfer', 'transfer', 'des', 'indn', 'po', 'dbt', 'crd', 'intl', 'usd',
  'recur', 'recurring', 'online', 'mobile', 'pmt', 'payme', 'withdrawal',
]);

// Dropped unless they are the leading token ("CO OP MARKET" keeps its "Co").
const CORP_WORDS = new Set([
  'llc', 'l.l.c', 'llp', 'inc', 'incorporated', 'corp', 'corporation', 'co',
  'ltd', 'company', 'plc', 'gmbh', 'store', 'stores', 'shop',
]);

// Bank descriptors end in "CITY ST". Codes that are also common English words
// (IN, OR, ME, HI, OK, DE, LA, PA, CO) are excluded — stripping them costs
// more than leaving them ("BEST BUY IN" must not become "Best").
const STATE_CODES = new Set([
  'al', 'ak', 'az', 'ar', 'ca', 'ct', 'dc', 'fl', 'ga', 'ia', 'id', 'il', 'ks',
  'ky', 'ma', 'md', 'mn', 'mo', 'ms', 'mt', 'nc', 'nd', 'ne', 'nh', 'nj', 'nm',
  'nv', 'ny', 'oh', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'va', 'vt', 'wa', 'wi',
  'wv', 'wy',
]);

const TRUNCATION_FIXES = {
  cooki: 'Cookies',
  grocer: 'Grocery',
  restaur: 'Restaurant',
  restaurnt: 'Restaurant',
  pharm: 'Pharmacy',
  pharmacy: 'Pharmacy',
  marketpl: 'Marketplace',
  mktpl: 'Marketplace',
  mktplace: 'Marketplace',
  mkt: 'Market',
  ctr: 'Center',
  cntr: 'Center',
  dept: 'Department',
  mgmt: 'Management',
  svc: 'Service',
  svcs: 'Services',
  hlth: 'Health',
  natl: 'National',
  intrntl: 'International',
  univ: 'University',
  assoc: 'Associates',
  whse: 'Warehouse',
  bkry: 'Bakery',
  sprmkt: 'Supermarket',
};

// Store and reference numbers: pure digits, or a token that is mostly digits
// ("F1234", "009eU", "2X9AB1CD3"). A single digit is kept so brands like
// "7-Eleven" and "3M" survive. Shared with the recurring series key, which
// strips the same volatile tokens for the same reason.
const isReference = isReferenceToken;

function isNoise(token) {
  return NOISE_WORDS.has(token.toLowerCase());
}

// A tight hyphen can glue a real word to noise ("Cooki-OSV"). Clean the parts
// rather than splitting the token, so "In-N-Out" and "T-Mobile" stay intact.
function cleanHyphenated(token) {
  if (!token.includes('-')) return token;
  const parts = token.split('-').filter(Boolean);
  // Short numeric parts belong to the brand ("7-Eleven", "3-Day Blinds"); only
  // a substantial reference chunk is worth dropping.
  const kept = parts.filter((p) => !isNoise(p) && !(p.length >= 3 && isReference(p)));
  if (kept.length === 0 || kept.length === parts.length) return token;
  return kept.join('-');
}

function stripLocation(tokens) {
  if (tokens.length < 3) return tokens;
  const last = tokens[tokens.length - 1].toLowerCase().replace(/[^a-z]/g, '');
  if (!STATE_CODES.has(last)) return tokens;
  // The token before a state code is the city in virtually every descriptor
  // that carries one. Requiring 3+ tokens keeps two-word brands whole.
  return tokens.slice(0, -2);
}

// ── 5. Casing ───────────────────────────────────────────────────────
const SPECIAL_CASE = {
  usps: 'USPS', ups: 'UPS', usa: 'USA', atm: 'ATM', cvs: 'CVS', bp: 'BP',
  ibm: 'IBM', hbo: 'HBO', kfc: 'KFC', ihop: 'IHOP', dmv: 'DMV', irs: 'IRS',
  nyc: 'NYC', dsw: 'DSW', rei: 'REI', amc: 'AMC', gnc: 'GNC', hvac: 'HVAC',
  ikea: 'IKEA', tjx: 'TJX', hp: 'HP', aaa: 'AAA', ymca: 'YMCA', usaa: 'USAA',
  pnc: 'PNC', td: 'TD', bbq: 'BBQ', dq: 'DQ', jcpenney: 'JCPenney',
  'at&t': 'AT&T', 'h&m': 'H&M', 'pg&e': 'PG&E', bjs: "BJ's", cfa: 'CFA',
  llc: 'LLC', ii: 'II', iii: 'III', nasa: 'NASA', tsa: 'TSA', dhl: 'DHL',
  fedex: 'FedEx', ebay: 'eBay', paypal: 'PayPal', doordash: 'DoorDash',
  gamestop: 'GameStop', petsmart: 'PetSmart', quiktrip: 'QuikTrip',
  racetrac: 'RaceTrac', winco: 'WinCo', youtube: 'YouTube', icloud: 'iCloud',
  openai: 'OpenAI', playstation: 'PlayStation', wifi: 'WiFi',
};

// Lowercased inside a name, never at the edges.
const SMALL_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'nor', 'of',
  'on', 'or', 'the', 'to', 'via', 'with',
]);

function capitalize(word) {
  if (!word) return word;
  return word[0].toUpperCase() + word.slice(1).toLowerCase();
}

function caseWord(word) {
  const lower = word.toLowerCase();
  if (SPECIAL_CASE[lower]) return SPECIAL_CASE[lower];

  // Intentional internal capitals from an already-clean source (Plaid's
  // merchant_name gives "DoorDash", "eBay") are a signal, not noise.
  if (/[a-z]/.test(word) && /[A-Z]/.test(word.slice(1))) return word;

  // "MCDONALD" → "McDonald", but leave "MCA" and short tokens alone.
  if (/^mc[a-z]{3,}$/i.test(word)) return `Mc${capitalize(word.slice(2))}`;

  // "O'REILLY" → "O'Reilly"; "JOE'S" → "Joe's" (possessive stays lowercase).
  if (word.includes("'")) {
    return word
      .split("'")
      .map((part, i) => (i > 0 && part.length <= 1 ? part.toLowerCase() : capitalize(part)))
      .join("'");
  }

  // Every hyphen part is a name part: "IN-N-OUT" → "In-N-Out". Interior small
  // words stay lowercase, as they would between spaces ("Direct-to-Consumer").
  if (word.includes('-')) {
    const parts = word.split('-');
    return parts
      .map((part, i) => {
        const isEdge = i === 0 || i === parts.length - 1;
        if (!isEdge && SMALL_WORDS.has(part.toLowerCase())) return part.toLowerCase();
        return capitalize(part);
      })
      .join('-');
  }

  // An all-caps token of 2-4 letters with no vowel is an initialism ("BBQ",
  // "DPT"); uppercase it rather than inventing "Bbq".
  if (/^[A-Z]{2,4}$/.test(word) && !/[AEIOUY]/.test(word)) return word;

  return capitalize(word);
}

function smartTitleCase(tokens) {
  return tokens
    .map((tok, i) => {
      const cased = caseWord(tok);
      const isEdge = i === 0 || i === tokens.length - 1;
      if (!isEdge && SMALL_WORDS.has(tok.toLowerCase())) return tok.toLowerCase();
      return cased;
    })
    .join(' ');
}

// ── Public API ──────────────────────────────────────────────────────

const MAX_TOKENS = 5;

/**
 * Human-readable merchant name for a raw bank description.
 * Returns '' for empty input; falls back to the trimmed original when
 * normalization would leave nothing behind.
 */
export function formatMerchantName(description) {
  const raw = String(description || '').trim().replace(/\s+/g, ' ');
  if (!raw) return '';

  const unwrapped = unwrapProcessor(raw);

  const alias = matchAlias(unwrapped);
  if (alias) return alias;

  let s = trimSegments(unwrapped);

  NOISE_PHRASES.forEach((re) => {
    s = s.replace(re, ' ');
  });

  s = s
    .replace(/[#]\s*\d+/g, ' ')
    // Reference numbers glued to the name with no space ("0423CITYWATER") —
    // strip a 2+ digit prefix when a real word (3+ letters) follows. Kept to
    // 2+ digits so single-digit brands ("7eleven") survive.
    .replace(/(^|\s)\d{2,}(?=[a-z]{3,})/gi, '$1')
    .replace(/[^\w&'\-.\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  let tokens = s.split(' ').filter(Boolean).map(cleanHyphenated);

  // Leading article: "THE LOCAL PUB" → "Local Pub".
  if (tokens.length > 1 && tokens[0].toLowerCase() === 'the') tokens = tokens.slice(1);

  tokens = tokens.filter((t) => !isNoise(t));

  // Keep reference-looking tokens only if they are all we have.
  const words = tokens.filter((t) => !isReference(t));
  tokens = words.length > 0 ? words : tokens;

  tokens = stripLocation(tokens);

  const meaningful = [];
  tokens.forEach((t) => {
    if (meaningful.length >= MAX_TOKENS) return;
    if (meaningful.length > 0 && CORP_WORDS.has(t.toLowerCase())) return;
    const lower = t.toLowerCase().replace(/[.]/g, '');
    meaningful.push(TRUNCATION_FIXES[lower] || t);
  });

  // A name never ends on a conjunction or article, whether it was truncated
  // by MAX_TOKENS or written that way.
  while (meaningful.length > 1 && SMALL_WORDS.has(meaningful[meaningful.length - 1].toLowerCase())) {
    meaningful.pop();
  }

  if (meaningful.length === 0) return raw;
  return smartTitleCase(meaningful);
}

/**
 * Canonical key for grouping transactions by merchant — category memory,
 * "seen this before" checks, and anything else that has to treat
 * "AMAZON MKTPLACE*1A2B" and "AMAZON.COM*9Z8Y" as the same place.
 *
 * Derived from formatMerchantName so the key and the visible name can never
 * drift apart: two rows that display identically always group together.
 */
export function merchantKey(description) {
  return formatMerchantName(description)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}
